import { yupResolver } from '@hookform/resolvers/yup';
import { useState } from 'react';
import { UseFormClearErrors } from 'react-hook-form';
import { ObjectSchema, Schema } from 'yup';
import * as yup from 'yup';
import { IPackstationForm } from 'pages/Checkout/CheckoutPackstation/types';
import { getRegexForCheckEmail } from 'modules/formatter';
import { useTranslations } from 'modules/i18n/useTranslations';
import { useSiteConfig } from 'modules/shop/siteConfig/hooks';
import { maxLength, minLength } from './constants';
import { Regex } from './index';
import { debouncedDhlValidation } from './utils';

export const useYupValidationResolver = (
  schema: ObjectSchema<any> | Record<string, Schema>,
) => {
  if (schema instanceof ObjectSchema) {
    return yupResolver(schema);
  } else {
    return yupResolver(yup.object(schema));
  }
};

export const useValidationRules = () => {
  const t = useTranslations();
  const { country } = useSiteConfig();

  return {
    firstName: yup.string().required(t('common.errors.general.required')),
    lastName: yup.string().required(t('common.errors.general.required')),
    street: yup
      .string()
      .matches(
        Regex.streetWithHouseNumber,
        t('common.errors.streetWithHouseNumber'),
      )
      .matches(
        Regex.streetWithHouseNumberWithoutSpace,
        t('common.errors.streetWithHouseNumberWithoutSpace'),
      )
      .required(t('common.errors.general.required')),
    zip: yup
      .string()
      .test({
        test: (zip) =>
          country && zip
            ? Regex.zipCodes[country].test(zip.toUpperCase())
            : false,
        message: t('common.errors.invalid-zip'),
      })
      .required(t('common.errors.general.required')),
    city: yup.string().required(t('common.errors.general.required')),
    email: yup
      .string()
      .max(maxLength.email, t('common.errors.email.maxLength'))
      .matches(Regex.email, t('common.errors.email.pattern'))
      .required(t('common.errors.general.required')),
    password: yup
      .string()
      .required(t('common.errors.general.required'))
      .matches(Regex.password, t('common.errors.password.number-and-letter')),
    emailCustom: (errorText?: string) =>
      yup.string().matches(Regex.email, errorText).required(errorText),
    tax: yup.string().matches(Regex.tax, t('common.errors.tax-number')),
    size: yup.string().min(2, t('common.errors.size.required')),
    newPassword: yup.string().when(['email', 'password'], (values, schema) => {
      const email = values?.[0];
      const password = values?.[1];
      const regex = getRegexForCheckEmail(email);

      if (!password.length) {
        return schema;
      }

      return schema
        .required(t('common.errors.general.required'))
        .test({
          test: (newPassword) => regex.test(newPassword),
          message: t('common.errors.password.not-email'),
        })
        .matches(Regex.password, t('common.errors.password.number-and-letter'));
    }),
    customerNumber: yup.string().required(t('common.errors.general.required')),
    orderNumber: yup.string().required(t('common.errors.general.required')),
    dhl: yup.string(),
  };
};

export const useDHLValidation = (
  clearError?: UseFormClearErrors<IPackstationForm>,
) => {
  /** hooks uses for asynchronous postal number validation
   * validation works only on fields it depends on
   */
  const t = useTranslations();
  const [state, updateState] = useState<Record<string, boolean>>({});

  return {
    dhl: yup
      .string()
      .matches(Regex.onlyNumber, t('common.errors.general.not-number'))
      .min(minLength.dhl, t('common.errors.general.length', { number: 6 }))
      .required(t('common.errors.general.required'))
      .test(
        'validate postal number',
        t('common.errors.invalid-postnumber'),
        async (postalCode, values) => {
          if (postalCode.length < minLength.dhl) {
            return true;
          }

          const { firstName, lastName } = values.parent;
          const key = `${firstName}-${lastName}-${postalCode}`;

          if (key in state) {
            return state[key];
          }

          const isValid = await debouncedDhlValidation({
            firstName,
            lastName,
            postalCode,
          });

          updateState({ [key]: isValid });

          if (isValid) {
            clearError?.('dhl');
          }

          return isValid;
        },
      ),
  };
};
